import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { useSupabaseAuth, useProgrammeAccess } from '../context/AuthContext';
import WorkspaceSidebar, { PROGRAMME_PILLARS } from '../components/WorkspaceSidebar';
import BreadcrumbBar from '../components/BreadcrumbBar';
import WorkspaceFrame from '../components/WorkspaceFrame';
import OrgSwitcher from '../components/OrgSwitcher';
import DemoLoginsPanel from '../components/DemoLoginsPanel';
import Loader from '../components/Loader';

/**
 * Layer 2 shell: programme-scoped sidebar + topbar, with nested pillar pages
 * rendered via <Outlet>. Loads the programme once and shares it (plus projects
 * and stage dictionary) with children through the outlet context.
 */
export default function ProgrammeShell() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, switchOrganisation } = useSupabaseAuth();
  const programmeAccess = useProgrammeAccess(id);

  const [programme, setProgramme] = useState(null);
  const [projects, setProjects] = useState([]);
  const [stages, setStages] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: prog } = await sb
        .from('programmes')
        .select('*, organisations(name), programme_statuses(name), programme_stages(name, order_index)')
        .eq('id', id)
        .maybeSingle();
      if (!prog) {
        setNotFound(true);
        return;
      }
      const [{ data: projs }, { data: stageRows }] = await Promise.all([
        sb.from('projects').select('id, name, target_go_live, programme_stages(name), programme_statuses(name)').eq('programme_id', id).order('created_at'),
        sb.from('programme_stages').select('id, name, order_index').order('order_index', { ascending: true }),
      ]);
      setProgramme(prog);
      setProjects(projs || []);
      setStages(stageRows || []);
    } catch (e) {
      console.error('[ProgrammeShell] load failed', e);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Org switcher data — all orgs this user belongs to (multi-org support).
  useEffect(() => {
    if (!user?.sub) return;
    let cancelled = false;
    (async () => {
      const { data } = await sb
        .from('profiles')
        .select('organisation_id, organisations(name, is_template)')
        .eq('auth_subject', user.sub);
      if (cancelled || !data) return;
      const seen = new Set();
      const list = [];
      data.forEach((row) => {
        if (row.organisation_id && !seen.has(row.organisation_id) && !row.organisations?.is_template) {
          seen.add(row.organisation_id);
          list.push({ id: row.organisation_id, name: row.organisations?.name || 'Organisation' });
        }
      });
      setOrgs(list);
    })();
    return () => { cancelled = true; };
  }, [user?.sub]);

  if (loading) return <Loader text="Opening programme…" />;

  if (notFound) {
    return (
      <div className="screen active">
        <div className="main">
          <div className="content">
            <div className="empty" style={{ marginTop: 40 }}>
              Programme not found, or you don't have access.
              <div style={{ marginTop: 12 }}>
                <button className="btn-ghost" onClick={() => navigate('/home')}>← All programmes</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sidebarRole = programmeAccess.isAdmin ? 'admin' : programmeAccess.isViewer ? 'viewer' : programmeAccess.hasAccess ? 'member' : 'viewer';
  const orgName = programme.organisations?.name || 'Organisation';
  const currentStageOrder = programme.programme_stages?.order_index ?? -1;

  return (
    <div className="screen active">
      <WorkspaceSidebar basePath={`/programme/${id}`} pillars={PROGRAMME_PILLARS} role={sidebarRole} subLabel="Programme rollup" />
      <div className="main">
        <BreadcrumbBar
          segments={[
            { label: orgName, onClick: () => navigate('/home') },
            { label: programme.name, current: true },
          ]}
        />

        <WorkspaceFrame
          rexBase={{
            layer: 'programme',
            orgName,
            programmeName: programme.name,
            projectCount: projects.length,
          }}
        >
          <div className="topbar">
            <OrgSwitcher
              orgs={orgs}
              currentOrgId={programme.organisation_id}
              currentOrgName={orgName}
              onSelect={(o) => switchOrganisation(o.id)}
            />
            <div className="badges">
              {programme.is_demo && <span className="b am">Demo</span>}
              <span className="b pu">{projects.length} project{projects.length !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {programme.is_demo && <DemoLoginsPanel programme={programme} />}

          <Outlet context={{ programme, projects, stages, currentStageOrder, reload: load, programmeAccess }} />
        </WorkspaceFrame>
      </div>
    </div>
  );
}
